import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { SettingsContext } from "../../../contexts/Settings";
import { UserContext } from "../../../contexts/User";
import { RestaurantContext } from "../../../contexts/Restaurant";
import { useCustomHooks } from "../../../functions/Hooks";
import { getSystemSettings, getCookie } from "../../../functions/Functions";
import { BASE_URL } from "../../../BaseUrl";
import "./Orderscreen.css";
import { MdFullscreen, MdVolumeUp } from "react-icons/md";

// Helper: Extract token / order number
const getToken = (order) => {
  const t =
    order?.token?.id ??
    (typeof order?.token === "string" || typeof order?.token === "number" ? order.token : null) ??
    order?.token_no ??
    order?.order_No ??
    "";
  return t ? String(t).trim() : "";
};

// Helper: Normalize token for comparisons
const normalizeToken = (t) => {
  const val = typeof t === "object" ? (t?.token?.id ?? t?.token ?? t?.id ?? t?.token_no ?? "") : t;
  return String(val || "").replace(/[^a-zA-Z0-9]/g, "").trim().toLowerCase();
};

// Helper: Dynamic font size class based on token length
const getSizeClass = (token) => {
  const len = String(token).length;
  if (len >= 7) return "oss-tile--sm";
  if (len >= 5) return "oss-tile--md";
  return "oss-tile--lg";
};

// Isolated Digital Clock to prevent 1-second re-renders of the entire screen
const DigitalClock = React.memo(() => {
  const [time, setTime] = useState("");
  useEffect(() => {
    const update = () =>
      setTime(
        new Date().toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
      );
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, []);
  return <div className="oss-clock">{time}</div>;
});

export default function Orderscreen() {
  const [preparingOrders, setPreparingOrders] = useState([]);
  const [readyOrders, setReadyOrders] = useState([]);
  const [logoError, setLogoError] = useState(false);
  const [callingTokens, setCallingTokens] = useState({});

  // Tracking refs to guarantee order state & announcements remain consistent
  const readyOrdersRef = useRef(new Set()); // IDs & tokens that reached READY (never demote on order update)
  const readyTimestampRef = useRef({}); // Timestamps of when orders became READY (ensures newest always at start)
  const announcedTokensRef = useRef(new Set()); // Voiced tokens cache
  const lastSoundCheckRef = useRef(0);
  const dispatchedOrdersRef = useRef(new Set());
  const isInitialLoadRef = useRef(true);
  const speechQueueRef = useRef([]);
  const isSpeakingRef = useRef(false);
  const fetchTimerRef = useRef(null);

  const { generalSettings, getSettings } = useContext(SettingsContext) || {};
  const { authUserInfo, selectedBranch } = useContext(UserContext) || {};
  const { firebaseAuth } = useCustomHooks();

  const [activeBranchId, setActiveBranchId] = useState(
    () =>
      new URLSearchParams(window.location.search).get("branch_id") ||
      selectedBranch?.id ||
      authUserInfo?.details?.branch_id ||
      localStorage.getItem("pos_selected_branch_id") ||
      null
  );

  // Preload speech synthesis voices on load
  useEffect(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => window.speechSynthesis.getVoices();
    }
    return () => { if (typeof window !== "undefined" && window.speechSynthesis) window.speechSynthesis.cancel(); };
  }, []);

  // Sequential female-voice speech synthesis queue processor
  const processSpeechQueue = useCallback(() => {
    if (typeof window === "undefined" || !window.speechSynthesis || isSpeakingRef.current || !speechQueueRef.current.length) return;
    const synth = window.speechSynthesis;
    const voices = synth.getVoices() || [];
    if (!voices.length) return setTimeout(processSpeechQueue, 150);

    const item = speechQueueRef.current.shift();
    if (!item) return;

    isSpeakingRef.current = true;
    try { synth.cancel(); } catch (e) {}

    const isCheck = item === "Sound check";
    const rawVal = typeof item === "object" ? (item?.token?.id ?? item?.token ?? item?.id ?? item?.token_no) : item;
    const cleanNum = String(rawVal || "").replace(/[^a-zA-Z0-9\s]/g, " ").trim();
    if (!isCheck && !cleanNum) { isSpeakingRef.current = false; return; }

    // Speak ticket codes character by character so adjacent digits aren't read as a whole number.
    const spokenToken = cleanNum.replace(/[a-zA-Z0-9]+/g, (part) =>
      [...part].join(" ")
    );

    const text = isCheck
      ? "Sound check. Now serving token number 3037"
      : `Now serving token number ${spokenToken}`;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.lang = "en-US";
    utterance.voice =
      voices.find((v) => /google us english|zira|aria|jenny|female/i.test(v.name)) ||
      voices.find((v) => v.lang?.startsWith("en") && !/david|mark|male/i.test(v.name)) ||
      voices[0];

    window._activeUtterance = utterance;
    let finishTimer = null;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(finishTimer);
      window._activeUtterance = null;
      isSpeakingRef.current = false;
      setTimeout(processSpeechQueue, 250);
    };

    utterance.onend = finish;
    utterance.onerror = finish;
    finishTimer = setTimeout(finish, 7000);
    try { synth.speak(utterance); } catch (e) { finish(); }
  }, []);

  // Voice announcement caller (deduplicated strictly once per token & order ID)
  const handleCallCustomer = useCallback((tokenVal, orderId = null) => {
    if (!tokenVal) return;
    const now = Date.now();

    if (tokenVal === "Sound check") {
      if (now - lastSoundCheckRef.current < 3000) return;
      lastSoundCheckRef.current = now;
      speechQueueRef.current = ["Sound check"];
      isSpeakingRef.current = false;
      return processSpeechQueue();
    }

    const clean = normalizeToken(tokenVal);
    const idKey = orderId ? `id_${orderId}` : null;
    if (!clean || announcedTokensRef.current.has(clean) || (idKey && announcedTokensRef.current.has(idKey))) return;

    announcedTokensRef.current.add(clean);
    if (idKey) announcedTokensRef.current.add(idKey);

    setCallingTokens((prev) => ({ ...prev, [clean]: true }));
    setTimeout(() => setCallingTokens((prev) => { const next = { ...prev }; delete next[clean]; return next; }), 2600);

    if (!speechQueueRef.current.some((i) => normalizeToken(i) === clean)) {
      speechQueueRef.current.push(clean);
    }
    processSpeechQueue();
  }, [processSpeechQueue]);

  // Load restaurant settings if missing
  useEffect(() => {
    if (!generalSettings && getSettings) {
      getSettings();
    }
  }, [generalSettings, getSettings]);

  // Background color lock (avoids white flicker)
  useEffect(() => {
    document.body.style.backgroundColor = "#0b0f0d";
    return () => {
      document.body.style.backgroundColor = "";
      if (typeof window !== "undefined" && window.speechSynthesis) {
        try { window.speechSynthesis.cancel(); } catch (e) {}
      }
    };
  }, []);

  const userBranchId = authUserInfo?.details?.branch_id;
  const currentBranchId = selectedBranch?.id;

  // Sync active branch if loaded late
  useEffect(() => {
    if (!activeBranchId) {
      const bId =
        currentBranchId ||
        userBranchId ||
        localStorage.getItem("pos_selected_branch_id");
      if (bId) setActiveBranchId(bId);
    }
  }, [userBranchId, currentBranchId, activeBranchId]);

  // Main order fetcher from backend
  const fetchOrders = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${getCookie()}` };
      const subRes = await axios.get(`${BASE_URL}/settings/get-submitted-orders`, { headers });
      const submitted = subRes.data?.[1] || subRes.data?.[0]?.data || subRes.data?.data || [];

      // Auto-detect branch ID if not yet set
      if (!activeBranchId && submitted?.[0]?.branch_id) {
        setActiveBranchId(submitted[0].branch_id);
      }

      let localFlow = {};
      try {
        localFlow = JSON.parse(localStorage.getItem("pos_order_flow_status") || "{}");
      } catch (e) {}

      const prep = [];
      const ready = [];

      submitted.forEach((item) => {
        if (!item || !item.id) return;
        const orderId = String(item.id);
        const token = getToken(item);
        const normToken = normalizeToken(token);

        const flow = String(localFlow[orderId] || "").trim().toUpperCase();
        const status = String(
          item.order_status ||
          item["order-status"] ||
          item.status ||
          item.kitchen_order_status ||
          item.kitchen_status ||
          ""
        ).trim().toUpperCase();

        // 1. Exclude dispatched, settled, cancelled, or refunded orders
        const isDispatchedOrSettled =
          dispatchedOrdersRef.current.has(orderId) ||
          parseInt(item.is_cancelled) === 1 ||
          parseInt(item.is_refund) === 1 ||
          parseInt(item.is_dispatched) === 1 ||
          parseInt(item.is_settled) === 1 ||
          parseInt(item.is_settle) === 1 ||
          flow.includes("DISPATCH") ||
          status.includes("DISPATCH") ||
          status.includes("SETTLE");

        if (isDispatchedOrSettled) {
          dispatchedOrdersRef.current.add(orderId);
          readyOrdersRef.current.delete(orderId);
          delete readyTimestampRef.current[orderId];
          if (normToken) {
            readyOrdersRef.current.delete(normToken);
            delete readyTimestampRef.current[normToken];
          }
          return;
        }

        if (!token) return;
        const entry = { id: item.id, token };

        // 2. CRUCIAL FIX: Once an order has reached READY, it NEVER reverts to Preparing,
        // even if items are added or deleted by cashier in POS.
        const isKnownReady =
          readyOrdersRef.current.has(orderId) ||
          (normToken && readyOrdersRef.current.has(normToken));

        const isReady =
          isKnownReady ||
          flow === "READY" ||
          parseInt(item.is_ready) === 1 ||
          status === "READY" ||
          status === "KR" ||
          status.includes("READY");

        if (isReady) {
          readyOrdersRef.current.add(orderId);
          if (normToken) readyOrdersRef.current.add(normToken);
          if (!readyTimestampRef.current[orderId]) {
            readyTimestampRef.current[orderId] =
              item.updated_at ? new Date(item.updated_at).getTime() : Number(item.id) || Date.now();
          }
          if (!ready.some((r) => String(r.id) === orderId || (normToken && normalizeToken(r.token) === normToken))) {
            ready.push(entry);
          }
        } else {
          if (!prep.some((p) => String(p.id) === orderId || (normToken && normalizeToken(p.token) === normToken))) {
            prep.push(entry);
          }
        }
      });

      // ALWAYS PLACE NEWEST READY ORDERS AT THE START (top-left)
      ready.sort((a, b) => {
        const timeA =
          readyTimestampRef.current[String(a.id)] ||
          readyTimestampRef.current[normalizeToken(a.token)] ||
          Number(a.id) ||
          0;
        const timeB =
          readyTimestampRef.current[String(b.id)] ||
          readyTimestampRef.current[normalizeToken(b.token)] ||
          Number(b.id) ||
          0;
        return timeB - timeA; // Descending order: highest timestamp / newest first!
      });

      // Mute initial existing ready orders so they do not voice on page start
      if (isInitialLoadRef.current) {
        ready.forEach((o) => {
          const key = normalizeToken(o.token);
          if (key) announcedTokensRef.current.add(key);
          if (o.id) announcedTokensRef.current.add(`id_${o.id}`);
        });
        isInitialLoadRef.current = false;
      } else {
        // Voice newly ready orders (strictly once)
        ready.forEach((o) => {
          if (o.token) handleCallCustomer(o.token, o.id);
        });
      }

      setPreparingOrders(prep);
      setReadyOrders(ready);
    } catch (err) {
      console.error("Orderscreen fetch error:", err);
    }
  }, [activeBranchId, handleCallCustomer]);

  const debouncedFetchOrders = useCallback(() => {
    clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(fetchOrders, 300);
  }, [fetchOrders]);

  // Real-time synchronization: IF branch real-time enabled use Firebase, ELSE use LocalStorage
  useEffect(() => {
    fetchOrders();

    let unsubPromise = null;
    const collection = generalSettings && getSystemSettings(generalSettings, "firebase_collection");

    // LocalStorage storage event listener (cross-tab sync on same machine)
    const handleStorageChange = (e) => {
      if (e.key === "pos_order_ready" && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          const { id, token } = data || {};
          if (id || token) {
            const strId = id ? String(id) : null;
            const normTok = token ? normalizeToken(token) : null;
            const now = Date.now();
            if (strId) {
              readyOrdersRef.current.add(strId);
              readyTimestampRef.current[strId] = now;
            }
            if (normTok) {
              readyOrdersRef.current.add(normTok);
              readyTimestampRef.current[normTok] = now;
            }

            setPreparingOrders((prev) =>
              prev.filter((o) => String(o.id) !== strId && normalizeToken(o.token) !== normTok)
            );
            if (token) {
              setReadyOrders((prev) => [
                { id, token },
                ...prev.filter((o) => String(o.id) !== strId && normalizeToken(o.token) !== normTok),
              ]);
              handleCallCustomer(token, id);
            }
          }
        } catch (err) {}
      } else if (e.key === "pos_order_dispatched" && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          const { id, token } = data || {};
          const strId = id ? String(id) : null;
          const normTok = token ? normalizeToken(token) : null;
          if (strId) dispatchedOrdersRef.current.add(strId);

          setPreparingOrders((prev) =>
            prev.filter((o) => String(o.id) !== strId && normalizeToken(o.token) !== normTok)
          );
          setReadyOrders((prev) =>
            prev.filter((o) => String(o.id) !== strId && normalizeToken(o.token) !== normTok)
          );
        } catch (err) {}
      } else if (e.key === "orderscreen_last_update" || e.key === "pos_order_flow_status") {
        debouncedFetchOrders();
      }
    };

    // 1. Instant local storage listener (for same-machine tab-to-tab sync)
    const handleStorageChangeWithLog = (e) => {
      if (
        e.key === "pos_order_ready" ||
        e.key === "pos_order_dispatched" ||
        e.key === "orderscreen_last_update" ||
        e.key === "pos_order_flow_status"
      ) {
        console.log("%c💾 [OrderScreen LocalStorage] Local tab event received:", "color: #ff9800; font-weight: bold;", e.key, e.newValue);
      }
      handleStorageChange(e);
    };
    window.addEventListener("storage", handleStorageChangeWithLog);

    // 2. Cross-device Cloud Firebase listener (real-time TV / KDS / POS updates)
    if (activeBranchId && collection && firebaseAuth) {
      
      unsubPromise = firebaseAuth(
        `pos/${collection}/order_tracker_screen/${activeBranchId}`,
        (error, res) => {
          if (error || !res) return;


          const rawEvents = (res?.order || res?.status)
            ? [res]
            : typeof res === "object"
            ? Object.values(res).filter((v) => v && typeof v === "object")
            : [];

          rawEvents.forEach((ev) => {
            if (!ev || typeof ev !== "object") return;
            const st = String(ev.order || ev.status || "").trim().toLowerCase();
            const evId = ev.orderId || ev.order_id || ev.id;
            const evToken = ev.token ? String(ev.token).trim() : "";
            const strId = evId ? String(evId) : null;
            const normTok = evToken ? normalizeToken(evToken) : null;

            if (st.includes("dispatch")) {
              if (strId) dispatchedOrdersRef.current.add(strId);
              setPreparingOrders((prev) =>
                prev.filter((o) => String(o.id) !== strId && normalizeToken(o.token) !== normTok)
              );
              setReadyOrders((prev) =>
                prev.filter((o) => String(o.id) !== strId && normalizeToken(o.token) !== normTok)
              );
            } else if (st.includes("ready") || st === "kr") {
              const now = Date.now();
              if (strId) {
                readyOrdersRef.current.add(strId);
                readyTimestampRef.current[strId] = now;
              }
              if (normTok) {
                readyOrdersRef.current.add(normTok);
                readyTimestampRef.current[normTok] = now;
              }

              setPreparingOrders((prev) =>
                prev.filter((o) => String(o.id) !== strId && normalizeToken(o.token) !== normTok)
              );
              if (evToken) {
                setReadyOrders((prev) => [
                  { id: evId, token: evToken },
                  ...prev.filter((o) => String(o.id) !== strId && normalizeToken(o.token) !== normTok),
                ]);
                handleCallCustomer(evToken, evId);
              }
            }
          });

          const hasDispatch = rawEvents.some((ev) =>
            String(ev?.order || ev?.status || "").toLowerCase().includes("dispatch")
          );
          if (!hasDispatch) debouncedFetchOrders();
        }
      );
    }

    // 3. Fallback auto-sync every 30 seconds
    const intervalTimer = setInterval(debouncedFetchOrders, 30000);

    return () => {
      clearInterval(intervalTimer);
      clearTimeout(fetchTimerRef.current);
      window.removeEventListener("storage", handleStorageChangeWithLog);
      if (unsubPromise) {
        unsubPromise.then((unsub) => {
          if (typeof unsub === "function") unsub();
        });
      }
    };
  }, [activeBranchId, generalSettings, firebaseAuth, fetchOrders, debouncedFetchOrders, handleCallCustomer]);

  // Fullscreen toggler
  const toggleFullScreen = () => {
    const elem = document.querySelector(".oss-root") || document.documentElement;
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      if (elem.requestFullscreen) elem.requestFullscreen();
      else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    }
  };

  const logoUrl = generalSettings ? getSystemSettings(generalSettings, "type_logo") : null;
  const brandName =
    (generalSettings &&
      (getSystemSettings(generalSettings, "siteName") ||
        getSystemSettings(generalSettings, "type_name"))) ||
    "Master Chef Grill & Gravy 21";

  return (
    <div className="oss-root">
      {/* Header */}
      <header className="oss-head">
        <div className="oss-brand">
          <Link to="/">
            {logoUrl && !logoError ? (
              <img
                src={logoUrl}
                alt="Logo"
                className="oss-logo-img"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="oss-logo" aria-hidden="true">
                {brandName.charAt(0) || "M"}
              </div>
            )}
          </Link>
          <div>
            <h1 className="oss-brand-name">{brandName}</h1>
            <p className="oss-brand-sub">Order Status</p>
          </div>
        </div>

        <div className="oss-header-right">
          <DigitalClock />

          <button
            type="button"
            className="oss-fullscreen-btn"
            onClick={() => handleCallCustomer("Sound check")}
            aria-label="Test voice announcement"
            title="Sound Check"
          >
            <MdVolumeUp size={20} />
          </button>

          <button
            type="button"
            className="oss-fullscreen-btn"
            onClick={toggleFullScreen}
            aria-label="Toggle fullscreen"
            title="Full Screen"
          >
            <MdFullscreen size={22} />
          </button>
        </div>
      </header>

      {/* Main Order Columns */}
      <main className="oss-cols">
        {/* Preparing Column */}
        <section className="oss-col is-prep">
          <div className="oss-col-head">
            <span className="oss-dot" aria-hidden="true"></span>
            <span className="oss-col-title">Preparing</span>
            <span className="oss-col-urdu" lang="ur" dir="rtl">
              تیار ہو رہا ہے
            </span>
            <span className="oss-count">{preparingOrders.length}</span>
          </div>
          <div className="oss-grid p-2">
            {preparingOrders.length === 0 ? (
              <div className="oss-empty">No orders currently preparing</div>
            ) : (
              preparingOrders.map((order, idx) => (
                <div
                  key={`${order.id || "prep"}-${order.token}-${idx}`}
                  className={`oss-tile ${getSizeClass(order.token)}`}
                >
                  {order.token}
                </div>
              ))
            )}
          </div>
        </section>

        {/* Ready for Pickup Column */}
        <section className="oss-col is-ready">
          <div className="oss-col-head">
            <span className="oss-dot" aria-hidden="true"></span>
            <span className="oss-col-title">Ready for Pickup</span>
            <span className="oss-col-urdu" lang="ur" dir="rtl">
              آرڈر تیار ہے
            </span>
            <span className="oss-count">{readyOrders.length}</span>
          </div>
          <div className="oss-grid p-2">
            {readyOrders.length === 0 ? (
              <div className="oss-empty">No orders ready for pickup</div>
            ) : (
              readyOrders.map((order, idx) => {
                const isBlinking = Boolean(callingTokens[normalizeToken(order.token)]);
                return (
                  <div
                    key={`${order.id || "ready"}-${order.token}-${idx}`}
                    className={`oss-tile is-ready-tile ${getSizeClass(order.token)} ${
                      isBlinking ? "is-blinking" : ""
                    }`}
                  >
                    {order.token}
                  </div>
                );
              })
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="oss-foot">
        <div className="oss-foot-info">
          <strong>Number on the green side?</strong> Please collect your order from the counter.
          <span className="oss-foot-urdu" lang="ur" dir="rtl">
            {" "}
            براہ کرم کاؤنٹر سے اپنا آرڈر وصول کریں۔
          </span>
        </div>
        <div className="oss-powered">
          Powered by <b>Indolj</b>
        </div>
      </footer>
    </div>
  );
}
